import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  message: string;
  type: ToastType;
  onClose: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ id, message, type, onClose }) => {
  useEffect(() => {
    const closeTimer = setTimeout(() => {
      onClose(id);
    }, 3500);

    return () => clearTimeout(closeTimer);
  }, [id, onClose]);

  const icons = {
    success: <CheckCircle className="text-success" size={20} />,
    error: <XCircle className="text-error" size={20} />,
    warning: <AlertCircle className="text-warning" size={20} />,
    info: <Info className="text-info" size={20} />,
  };

  const borderColors = {
    success: 'border-success',
    error: 'border-error',
    warning: 'border-warning',
    info: 'border-info',
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 110 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 110 }}
      transition={{ type: 'spring', damping: 20, stiffness: 150 }}
      className={`pointer-events-auto bg-white rounded-xl p-4 border-l-4 shadow-[0_8px_32px_rgba(0,0,0,0.14),0_2px_8px_rgba(0,0,0,0.08)] flex items-start gap-3 relative overflow-hidden ${borderColors[type]}`}
    >
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center bg-gray-50">
        {icons[type]}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[#0F172A] leading-tight">{message}</p>
        <p className="text-xs text-[#64748B] mt-1">Status updated successfully</p>
      </div>
      <button 
        onClick={() => onClose(id)}
        className="text-[#94A3B8] hover:text-[#374151] transition-colors mt-0.5"
      >
        <X size={18} />
      </button>
      
      <motion.div 
        initial={{ width: '100%' }}
        animate={{ width: 0 }}
        transition={{ duration: 3.5, ease: 'linear' }}
        className="absolute bottom-0 left-0 h-[3px] bg-current opacity-30"
        style={{ color: `var(--color-${type})` }}
      />
    </motion.div>
  );
};

export const ToastContainer: React.FC<{ toasts: any[], onClose: (id: string) => void }> = ({ toasts, onClose }) => {
  return (
    <div className="fixed top-4 right-4 z-[70] flex flex-col gap-[10px] pointer-events-none max-w-[380px] w-[calc(100vw-32px)]">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={onClose} />
        ))}
      </AnimatePresence>
    </div>
  );
};
